// components/InlineImage.tsx — renders the [[image:KIND:ID]] markup the rebuilt
// Prof emits per spec §4.3 (Pre-Socio Rebuild Session A, Task 7).
//
// Three markup forms:
//   - [[image:sign:NAME]]      — Italian semantic name; resolved via the FE-side
//                                stripped lookup (`image-name-lookup.json`).
//   - [[image:question:Q12345]] — question_id; for Session A the screen-level
//                                renderer can hand the resolved imageRef in via
//                                the optional `imageRef` prop. Otherwise we render
//                                a localized placeholder.
//   - [[image:scenario:NAME]]  — reserved for v1.2 (scenario diagrams). Renders a
//                                placeholder + console warning per dispatch contract.
//
// WCAG 1.4.1: every <img> carries a meaningful Italian alt — the resolved
// italianName when available, or a fallback. Italian "tu" voice on user-facing
// fallback strings.
//
// Layout (founder dispatch 2026-07-07, pilot phone test): images sit INLINE in
// the message flow as compact thumbnails — an `inline-block` wrapper so a run
// of several signs wraps into a small gallery instead of a screen-tall stack —
// and every image is tappable, opening a fullscreen lightbox (portal over the
// app clip) with an explicit close. This replaced the original "block wrapper,
// non-clickable" dispatch after the founder asked for ChatGPT/Claude-style
// in-flow images.

import { useEffect, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { useTokens } from '../state/theme';
import imageNameLookup from '../data/image-name-lookup.json';

export type InlineImageKind = 'sign' | 'question' | 'scenario';

export interface InlineImageProps {
  kind: InlineImageKind;
  /**
   * For kind='sign'   — the Italian semantic name (e.g. "Limite minimo di velocità — 30 km/h")
   * For kind='question' — the question_id (e.g. "Q12345"); resolution happens via the
   *   optional `imageRef` prop the screen-level renderer passes in.
   * For kind='scenario' — a scenario name (placeholder until v1.2).
   */
  id: string;
  /**
   * Optional direct image ref override — when the calling screen already knows the
   * canonical PNG path (e.g. Allena/Chiedi screens get `imageRef` from the question
   * payload), pass it in to short-circuit the lookup. Path form: "img_sign_by_hash/HASH.png"
   * or "img_sign/N.png" (the same form the BE emits today).
   */
  imageRef?: string | null;
  /**
   * Optional alt-text override — useful when the caller has richer context than the
   * lookup table (e.g. the question's argomentoName). Defaults to the resolved
   * italianName, or an Italian fallback when nothing is known.
   */
  altOverride?: string;
  /**
   * Guided structured refs reuse sign assets as scenario illustrations. If one is
   * missing, the lesson should collapse the frame instead of showing a scenario
   * placeholder box in the middle of prose.
   */
  hideWhenBroken?: boolean;
  /**
   * Thumbnail cap in px (default 160). The voice-call visual strip passes a
   * smaller cap so a set of signs stays a single row above the transcript.
   */
  maxWidth?: number;
}

interface ResolvedImage {
  imageRef: string;
  alt: string;
}

// Mirror of build-time normalizer in scripts/build-image-name-lookup.js.
function normalizeName(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

interface LookupEntry { imageId: string; italianName: string }
const LOOKUP = imageNameLookup as Record<string, LookupEntry>;

// Item 4 (founder round-3, 2026-06-11) — the chat answer for "Spiegami i segnali
// di divieto di sorpasso" showed 2 real images but "[immagine non disponibile]"
// for "fine del divieto di sorpasso", a sign the lookup HAS. The miss was a
// NORMALIZATION mismatch: the lookup keys are built with `normalizeName` (which
// PRESERVES accents/apostrophes), while the BE canonicalizes its emitted markers
// with `normalizeSignName` (lowercase, STRIP accents, STRIP quotes, collapse
// whitespace — see backend/src/gateway/chiedi-v2-core.ts). So when the pack's
// canonical spelling carries an accent/apostrophe or an extra qualifier (e.g.
// "(generale)") that the lookup key doesn't, the FE exact match misses on a sign
// that demonstrably exists.
//
// Fix: a SECOND index keyed by the BE's normalizeSignName semantics, built once
// from the lookup keys. resolveSign tries (1) the exact normalizeName key, (2)
// the BE-normalized key, (3) the alias table, then (4) a contains/startsWith
// fallback over the normalized keys — only THEN the honest placeholder.

/** Mirror of backend `normalizeSignName` (chiedi-v2-core.ts): lowercase, strip
 *  accents, strip quotes, collapse whitespace, trim. The semantics the BE uses
 *  to canonicalize the [[image:sign:…]] markers it emits. */
function normalizeSignNameBE(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining accents
    .replace(/['‘’"`]/g, '') // strip quotes/apostrophes
    .replace(/\s+/g, ' ')
    .trim();
}

/** BE-normalized index of every lookup key → its entry. Built once at module
 *  load. First-write-wins on a collision (the canonical accented key already
 *  resolves via LOOKUP; this index only adds reach for accent/quote/qualifier
 *  variants the exact path misses). */
const LOOKUP_BY_BE_NORM: Record<string, LookupEntry> = (() => {
  const out: Record<string, LookupEntry> = {};
  for (const [key, entry] of Object.entries(LOOKUP)) {
    const norm = normalizeSignNameBE(key);
    if (!(norm in out)) out[norm] = entry;
  }
  return out;
})();

/** The BE-normalized keys, longest-first so the contains/startsWith fallback
 *  prefers the most specific match (e.g. "fine del divieto di sorpasso per
 *  autocarri" over "fine del divieto di sorpasso") when the query is a superset. */
const LOOKUP_BE_NORM_KEYS: string[] = Object.keys(LOOKUP_BY_BE_NORM).sort(
  (a, b) => b.length - a.length,
);

/**
 * Item 4 — last-resort fuzzy resolver over the BE-normalized keys. Returns the
 * entry whose normalized key is the longest one that either CONTAINS the query
 * or is CONTAINED BY the query (so "fine del divieto di sorpasso (generale)"
 * matches the key "fine del divieto di sorpasso", and a query that drops a
 * qualifier still hits a more specific key). Guards against trivially-short
 * queries to avoid spurious substring hits.
 */
function fuzzyResolveSign(queryNorm: string): LookupEntry | null {
  if (queryNorm.length < 4) return null;
  for (const key of LOOKUP_BE_NORM_KEYS) {
    if (key.length < 4) continue;
    // Superset direction (SAFE): the query carries an extra qualifier the key
    // doesn't, e.g. "fine del divieto di sorpasso (generale)" ⊇ the key.
    if (queryNorm.includes(key)) return LOOKUP_BY_BE_NORM[key];
    // Truncated direction (the model emitted a partial name): only accept when
    // the query is a substantial multi-word fragment (≥ 8 chars) so a bare
    // single word ("divieto") can't spuriously match a longer sign key.
    if (queryNorm.length >= 8 && key.includes(queryNorm)) return LOOKUP_BY_BE_NORM[key];
  }
  return null;
}

const COMMON_SIGN_ALIASES: Record<string, string> = {
  stop: "schema didattico: croce di sant'andrea (passaggio a livello) e segnale stop",
  tram: 'attraversamento tranviario',
  'dare precedenza': 'dare la precedenza',
  'divieto di transito': 'transito vietato in entrambi i sensi (divieto di transito)',
  'transito vietato': 'transito vietato in entrambi i sensi (divieto di transito)',
  'divieto di accesso': 'senso vietato',
  'accesso vietato': 'senso vietato',
  'fine diritto di precedenza': 'fine del diritto di precedenza',
  'diritto di precedenza': 'diritto di precedenza (strada con diritto di precedenza)',
  'strada con diritto di precedenza': 'diritto di precedenza (strada con diritto di precedenza)',
  'curva pericolosa': 'curva pericolosa a destra',
  'passaggio a livello con barriere': 'passaggio a livello con barriere o semibarriere',
  'doppio senso di circolazione': 'doppio senso di circolazione',
  'limite massimo di velocita': 'limite massimo di velocità — 80 km/h',
  'limite massimo di velocità': 'limite massimo di velocità — 80 km/h',
  'limite minimo di velocita': 'limite minimo di velocità — 30 km/h',
  'limite minimo di velocità': 'limite minimo di velocità — 30 km/h',
  'alt dogana': 'confine doganale (alt dogana)',
  'fine prescrizione': 'fine di tutti i divieti precedentemente segnalati',
  'divieto trasporto merci pericolose': 'transito vietato ai veicoli che trasportano merci pericolose',
  'transito vietato veicoli a trazione animale': 'transito vietato ai veicoli a trazione animale',
  'dare precedenza nei sensi unici alternati': 'dare precedenza nei sensi unici alternati',
  'senso unico': 'senso unico parallelo (frontale)',
};

/** Exported for the content-marker audit (scripts/audit-sign-markers.mts) and
 *  tests: the EXACT resolution chain the UI uses, so an offline sweep over the
 *  corpus reports precisely what would render "[immagine non disponibile]". */
export function resolveSign(name: string): ResolvedImage | null {
  const normalized = normalizeName(name);
  const aliasTarget = COMMON_SIGN_ALIASES[normalized];
  // C1a resolver priority: curated aliases win before generated lookup aliases.
  // (1) exact normalizeName key (accent-preserving — today's matches), then
  // (2) the alias table (also keyed by normalizeName).
  let entry: LookupEntry | undefined = aliasTarget ? LOOKUP[aliasTarget] : undefined;
  entry ??= LOOKUP[normalized];
  // (3) Item 4 — the BE-normalized exact match (strips accents/quotes). This is
  // what fixes "fine del divieto di sorpasso" + accent/apostrophe variants whose
  // canonical pack spelling differs from the lookup key only by an accent/quote.
  if (!entry) {
    const beNorm = normalizeSignNameBE(name);
    entry = aliasTarget ? LOOKUP_BY_BE_NORM[normalizeSignNameBE(aliasTarget)] : undefined;
    entry ??= LOOKUP_BY_BE_NORM[beNorm];
    // (4) contains/startsWith fallback over the normalized keys (handles extra
    // qualifiers like "(generale)" the lookup key doesn't carry) — last resort
    // before the honest placeholder.
    if (!entry) entry = fuzzyResolveSign(beNorm) ?? undefined;
  }
  if (!entry) return null;
  return { imageRef: entry.imageId, alt: entry.italianName };
}

// C1 (P5 ghost-image bug): when the Prof emits a [[image:sign:NAME]] whose NAME
// does not resolve (invented PascalCase id, English name, raw numeric id), the
// component degrades to the "[immagine non disponibile]" placeholder. That
// degrade used to be SILENT — it sailed through shadow-Prof smokes and shipped a
// broken box next to text that says "guarda il segnale". We now warn once per
// unique unresolved name so the dev console + smokes surface it. Dedup keeps a
// repeated marker from spamming the console across re-renders.
const warnedSignMisses = new Set<string>();
function warnSignResolveMiss(name: string): void {
  if (warnedSignMisses.has(name)) return;
  warnedSignMisses.add(name);
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(
      `[InlineImage] sign markup did not resolve to a known image: "${name}". ` +
        'Use the lowercase Italian sign name from image-name-lookup.json — not a ' +
        'CamelCase/English/numeric id. Rendering placeholder.',
    );
  }
}

/**
 * Renders a localized "image not available" placeholder. Used when:
 *  - the lookup misses (sign name unknown),
 *  - a question_id is given without an imageRef prop,
 *  - the markup is for a scenario (deferred to v1.2).
 *
 * Italian "tu"-voice copy. WCAG: aria-label mirrors the visible text so screen
 * readers convey the same fallback meaning.
 */
function ImagePlaceholder({
  message,
  ariaLabel,
}: {
  message: string;
  ariaLabel: string;
}): JSX.Element {
  const tokens = useTokens();
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        margin: '4px 2px',
        padding: '6px 10px',
        background: tokens.surface2,
        border: `1px dashed ${tokens.line}`,
        borderRadius: tokens.radius.md,
        color: tokens.textFaint,
        fontFamily: tokens.fonts.mono,
        fontSize: 11,
        letterSpacing: 0.2,
      }}
    >
      {message}
    </span>
  );
}

/**
 * Fullscreen zoom overlay — portal'd to <body> so it escapes the AppFrame
 * overflow clip. Tap anywhere (or the ×, or Escape) closes it.
 */
function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      data-testid="image-lightbox"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 20,
        background: 'rgba(12,9,26,0.88)',
        cursor: 'zoom-out',
      }}
    >
      <button
        type="button"
        aria-label="Chiudi immagine"
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 'calc(10px + var(--sai-top, 0px))',
          right: 12,
          width: 44,
          height: 44,
          borderRadius: 22,
          border: 'none',
          background: 'rgba(255,255,255,0.14)',
          color: '#fff',
          fontSize: 22,
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
      <img
        src={src}
        alt={alt}
        style={{ maxWidth: '92vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 10 }}
      />
      <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13.5, textAlign: 'center', maxWidth: '86vw' }}>
        {alt}
      </span>
    </div>,
    document.body,
  );
}

export function InlineImage({
  kind,
  id,
  imageRef,
  altOverride,
  hideWhenBroken = false,
  maxWidth = 160,
}: InlineImageProps): JSX.Element | null {
  const tokens = useTokens();
  const [broken, setBroken] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  // Resolve to a concrete imageRef + alt depending on the markup kind.
  let resolved: ResolvedImage | null = null;

  if (imageRef) {
    // Direct override — caller already knows the path.
    resolved = { imageRef, alt: altOverride ?? id };
  } else if (kind === 'sign') {
    resolved = resolveSign(id);
    if (resolved && altOverride) resolved = { ...resolved, alt: altOverride };
    if (!resolved) warnSignResolveMiss(id);
  } else if (kind === 'question') {
    // Without an imageRef prop we cannot render the question's image at this
    // layer — defer to the screen-level renderer per dispatch contract.
    resolved = null;
  } else if (kind === 'scenario') {
    // Reserved for v1.2 — log once-per-render so the dev console flags the
    // deferred path during shadow-Prof smokes.
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        `[InlineImage] scenario markup is reserved for v1.2 — rendering placeholder for "${id}"`,
      );
    }
    resolved = null;
  }

  if (!resolved || broken) {
    if (hideWhenBroken) return null;
    if (kind === 'question') {
      return (
        <ImagePlaceholder
          message="[immagine domanda non disponibile]"
          ariaLabel="Immagine della domanda non disponibile"
        />
      );
    }
    if (kind === 'scenario') {
      return (
        <ImagePlaceholder
          message="[diagramma scenario non disponibile]"
          ariaLabel="Diagramma dello scenario non disponibile"
        />
      );
    }
    // Unknown sign name — graceful fallback.
    return (
      <ImagePlaceholder
        message="[immagine non disponibile]"
        ariaLabel={`Immagine non disponibile: ${id}`}
      />
    );
  }

  // Public path mirrors the existing QuestionCard pattern (line 143):
  // `<img src={`/${question.imageRef}`} />`. The PNG is served by Vite/Pages
  // out of `/public/img_sign_by_hash/` or `/public/img_sign/`.
  const src = `/${resolved.imageRef}`;
  return (
    <span
      style={{
        display: 'inline-block',
        verticalAlign: 'top',
        maxWidth: '100%',
        margin: '6px 8px 6px 0',
      }}
    >
      <button
        type="button"
        onClick={() => setZoomed(true)}
        aria-label={`Ingrandisci: ${resolved.alt}`}
        style={{
          display: 'block',
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'zoom-in',
        }}
      >
        <img
          src={src}
          alt={resolved.alt}
          style={{
            display: 'block',
            maxWidth,
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: tokens.radius.sm,
          }}
          decoding="async"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      </button>
      {zoomed && <ImageLightbox src={src} alt={resolved.alt} onClose={() => setZoomed(false)} />}
    </span>
  );
}
